import { useImage } from "@hooks/useImage";
import { useRichText } from "@hooks/useRichText";
import { useRoutes } from "@hooks/useRoutes";
import {
  type ComponentProps,
  type FromSanityGraphQL,
  type ResolvedLink,
} from "@ts/components";
import {
  CustomImage,
  SectionUniqueSellingPoint,
  UniqueSellingPoint,
} from "@ts/sanity";
import React, { FC, ReactNode } from "react";

type USPInputProps = ComponentProps &
  FromSanityGraphQL<SectionUniqueSellingPoint>;

type USPOutputProps = Omit<USPInputProps, "sections" | "autoDelay"> & {
  sections?: NormalisedSection[];
  autoDelay: object | false;
};

type NormalisedSection = Omit<
  UniqueSellingPoint,
  "image" | "link" | "content"
> & {
  link: ResolvedLink;
  content: ReactNode | string;
  image?: CustomImage & {
    images: {
      fallback: {
        src: string;
      };
    };
  };
};

export const withUniqueSellingPoint =
  (Component: FC<USPOutputProps>) =>
  ({
    name = "UniqueSellingPoint",
    sections,
    autoDelay,
    ...props
  }: USPInputProps) => {
    const { linkResolver } = useRoutes();
    const { getFluidImage } = useImage();
    const { parseContent } = useRichText();
    const normalisedAutoDelay =
      Number(autoDelay) > 0 ? { delay: Number(autoDelay) } : false;
    const normalisedSections = sections?.map((section: UniqueSellingPoint) => ({
      ...section,
      link: linkResolver(section.link?.[0]),
      content: parseContent(section.content),
      image: getFluidImage(section.image, {
        width: 128,
        height: 128,
        layout: "constrained",
      }),
    }));

    Component.displayName = name;

    if (!sections?.length) {
      return null;
    }

    return (
      <Component
        {...props}
        sections={normalisedSections}
        autoDelay={normalisedAutoDelay}
      />
    );
  };
